/* Lumiloo rebuild – shared behavior for sr-* sections */
(() => {
  const selected = (window.srSelected = window.srSelected || {});
  const lineProps = (window.srProps = window.srProps || {});

  const formatMoney = (cents) => {
    const fmt = (window.srMoneyFormat || '${{amount}}');
    const amount = (cents / 100).toFixed(2);
    return fmt.replace(/\{\{\s*amount\s*\}\}/, amount.replace(/\B(?=(\d{3})+(?!\d))/g, ','))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100));
  };

  /* Shipping protection: added once with the first add to cart; if the customer removes it, it stays removed until the cart is emptied. */
  const PROTECTION_KEY = 'sr-protection-added';
  async function shouldAddProtection(variantId) {
    const prot = window.srProtection;
    if (!prot || !prot.variantId || String(prot.variantId) === String(variantId)) return false;
    let cart;
    try {
      const res = await fetch(((window.Shopify && Shopify.routes && Shopify.routes.root) || '/') + 'cart.js', { headers: { Accept: 'application/json' } });
      cart = await res.json();
    } catch (e) { return false; }
    let flag = null;
    try { flag = sessionStorage.getItem(PROTECTION_KEY); } catch (e) {}
    if (!cart.item_count) { flag = null; try { sessionStorage.removeItem(PROTECTION_KEY); } catch (e) {} }
    const inCart = (cart.items || []).some((i) => String(i.variant_id) === String(prot.variantId));
    if (inCart || flag) return false;
    return true;
  }
  const markProtectionAdded = () => { try { sessionStorage.setItem(PROTECTION_KEY, '1'); } catch (e) {} };

  async function addToCart(variantId, quantity = 1, properties = {}) {
    const entries = Object.entries(properties || {});
    const addProtection = await shouldAddProtection(variantId);
    const actions = window.Shopify && window.Shopify.actions;
    if (actions && actions.updateCart) {
      const line = { merchandiseId: String(variantId), quantity };
      if (entries.length) line.attributes = entries.map(([key, value]) => ({ key, value: String(value) }));
      const lines = [line];
      if (addProtection) lines.push({ merchandiseId: String(window.srProtection.variantId), quantity: 1 });
      const result = await actions.updateCart(
        { lines },
        { event: { context: 'product' } }
      );
      if (result && result.userErrors && result.userErrors.length) throw new Error(result.userErrors[0].message);
      if (addProtection) markProtectionAdded();
      // Horizon's drawer opens itself after it re-renders when auto-open is on; opening it here would flash an empty cart.
      if (actions.openCart && !document.querySelector('cart-drawer-component[auto-open]')) actions.openCart();
      return;
    }
    const item = { id: Number(variantId), quantity };
    if (entries.length) item.properties = Object.fromEntries(entries);
    const items = [item];
    if (addProtection) items.push({ id: Number(window.srProtection.variantId), quantity: 1 });
    const res = await fetch((window.Theme && Theme.routes.cart_add_url) || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    if (!res.ok) throw new Error((await res.json()).description || 'Could not add to cart');
    if (addProtection) markProtectionAdded();
    window.location.href = (window.Theme && Theme.routes.cart_url) || '/cart';
  }

  /* <sr-atc data-product-id data-variant-id> wraps an add-to-cart button */
  class SrAtc extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button || this._bound) return;
      this._bound = true;
      this.button.addEventListener('click', this.onClick.bind(this));
      this.onChange = (e) => {
        if (String(e.detail.productId) !== this.dataset.productId) return;
        this.dataset.variantId = e.detail.variantId;
        const price = this.querySelector('[data-sr-price]');
        const compare = this.querySelector('[data-sr-compare]');
        if (price) price.textContent = formatMoney(e.detail.price);
        if (compare) {
          compare.textContent = e.detail.compare > e.detail.price ? formatMoney(e.detail.compare) : '';
        }
      };
      document.addEventListener('sr:variant-change', this.onChange);
    }
    disconnectedCallback() { document.removeEventListener('sr:variant-change', this.onChange); this._bound = false; }
    async onClick(e) {
      e.preventDefault();
      const variantId = selected[this.dataset.productId] || this.dataset.variantId;
      if (!variantId || this.button.getAttribute('aria-busy') === 'true') return;
      this.button.setAttribute('aria-busy', 'true');
      try {
        await addToCart(variantId, 1, lineProps[this.dataset.productId]);
      } catch (err) {
        console.error('[sr-atc]', err);
        alert(err.message || 'Something went wrong. Please try again.');
      } finally {
        this.button.removeAttribute('aria-busy');
      }
    }
  }

  /* <sr-bundle data-product-id> holds radio inputs (value = variant id) and an optional per-pillow size picker */
  class SrBundle extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.addEventListener('change', (e) => {
        if (e.target.matches('select[data-sr-size]')) { this.collectSizes(); return; }
        this.emit();
      });
      this.emit();
    }
    emit() {
      const input = this.querySelector('input[type="radio"]:checked');
      if (!input) return;
      selected[this.dataset.productId] = input.value;
      this.querySelectorAll('.sr-tier').forEach((t) => t.classList.toggle('is-active', t.contains(input)));
      this.renderSizes(Math.max(1, Number(input.dataset.qty) || 1));
      document.dispatchEvent(new CustomEvent('sr:variant-change', {
        detail: {
          productId: this.dataset.productId,
          variantId: input.value,
          price: Number(input.dataset.price),
          compare: Number(input.dataset.compare || 0),
        },
      }));
    }
    renderSizes(count) {
      const box = this.querySelector('[data-sr-sizes]');
      const rows = box && box.querySelector('[data-sr-size-rows]');
      if (!rows) { delete lineProps[this.dataset.productId]; return; }
      const options = (box.dataset.options || '').split(',').map((o) => o.trim()).filter(Boolean);
      const label = box.dataset.label || "Child's Age";
      const previous = [...rows.querySelectorAll('select')].map((s) => s.value);
      rows.textContent = '';
      for (let i = 1; i <= count; i++) {
        const row = document.createElement('label');
        row.className = 'sr-sizes__row';
        const num = document.createElement('span');
        num.textContent = '#' + i;
        const select = document.createElement('select');
        select.setAttribute('data-sr-size', '');
        select.setAttribute('aria-label', label + ' for pillow ' + i);
        options.forEach((o) => {
          const opt = document.createElement('option');
          opt.value = o;
          opt.textContent = o;
          select.appendChild(opt);
        });
        if (previous[i - 1] && options.includes(previous[i - 1])) select.value = previous[i - 1];
        row.append(num, select);
        rows.appendChild(row);
      }
      this.collectSizes();
    }
    collectSizes() {
      const box = this.querySelector('[data-sr-sizes]');
      if (!box) return;
      const label = box.dataset.label || "Child's Age";
      const props = {};
      box.querySelectorAll('select[data-sr-size]').forEach((s, i) => { props['Pillow ' + (i + 1) + ' - ' + label] = s.value; });
      lineProps[this.dataset.productId] = props;
    }
  }

  /* <sr-gallery> main image + thumbs + arrows */
  class SrGallery extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.thumbs = [...this.querySelectorAll('[data-thumb]')];
      this.index = 0;
      this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.go(i)));
      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1));
    }
    go(i) {
      const n = this.slides.length;
      if (!n) return;
      this.index = (i + n) % n;
      this.slides.forEach((s, k) => (s.hidden = k !== this.index));
      this.thumbs.forEach((t, k) => t.setAttribute('aria-current', k === this.index));
    }
  }

  /* <sr-countdown data-hours="6"> evergreen timer that restarts per browser session */
  class SrCountdown extends HTMLElement {
    connectedCallback() {
      const ms = Number(this.dataset.hours || 6) * 3600e3;
      const key = 'sr-countdown-' + (this.dataset.key || 'default');
      let end = 0;
      try { end = Number(sessionStorage.getItem(key)); } catch (e) {}
      if (!end || end < Date.now()) {
        end = Date.now() + ms;
        try { sessionStorage.setItem(key, end); } catch (e) {}
      }
      const out = this.querySelector('[data-time]') || this;
      const pad = (v) => String(v).padStart(2, '0');
      const tick = () => {
        const left = Math.max(0, end - Date.now());
        const h = Math.floor(left / 3600e3), m = Math.floor((left % 3600e3) / 60e3), s = Math.floor((left % 60e3) / 1e3);
        out.textContent = `${pad(h)} Hr : ${pad(m)} Min : ${pad(s)} Sec`;
      };
      tick();
      clearInterval(this._t);
      this._t = setInterval(tick, 1000);
    }
    disconnectedCallback() { clearInterval(this._t); }
  }

  /* <sr-video-card> tap to play / pause */
  class SrVideoCard extends HTMLElement {
    connectedCallback() {
      const video = this.querySelector('video');
      const btn = this.querySelector('[data-play]');
      if (!video || !btn || this._bound) return;
      this._bound = true;
      btn.addEventListener('click', () => {
        if (video.paused) { video.play(); this.classList.add('is-playing'); }
        else { video.pause(); this.classList.remove('is-playing'); }
      });
    }
  }

  /* <sr-sticky-atc> shows once the main add-to-cart scrolls out of view */
  class SrSticky extends HTMLElement {
    connectedCallback() {
      const target = document.querySelector('[data-sr-main-atc]');
      if (!target || !('IntersectionObserver' in window)) { this.classList.add('is-visible'); return; }
      this._io = new IntersectionObserver(([entry]) => {
        this.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
      this._io.observe(target);
    }
    disconnectedCallback() { this._io?.disconnect(); }
  }

  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };
  define('sr-atc', SrAtc);
  define('sr-bundle', SrBundle);
  define('sr-gallery', SrGallery);
  define('sr-countdown', SrCountdown);
  define('sr-video-card', SrVideoCard);
  define('sr-sticky-atc', SrSticky);

  /* Scroll reveal */
  const reveal = (root = document) => {
    const items = root.querySelectorAll('.sr-reveal:not(.is-visible)');
    if (!('IntersectionObserver' in window)) { items.forEach((el) => el.classList.add('is-visible')); return; }
    const io = new IntersectionObserver((entries) => entries.forEach((en) => {
      if (en.isIntersecting) { en.target.classList.add('is-visible'); io.unobserve(en.target); }
    }), { rootMargin: '0px 0px -8% 0px' });
    items.forEach((el) => io.observe(el));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => reveal());
  else reveal();
  document.addEventListener('shopify:section:load', (e) => reveal(e.target));

  /* Theme editor: open accordion / show slide for the selected block */
  document.addEventListener('shopify:block:select', (e) => {
    const details = e.target.closest('details') || e.target.querySelector?.('details');
    if (details) details.open = true;
  });
})();
